import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { api, type TemplateSummary } from '../../lib/api';
import { BUILTIN_TEMPLATES, getBuiltinTemplate } from '../../lib/companyTemplates';
import { navigate, paths } from '../../lib/router';
import { useDeckStore } from '../../store/deckStore';

export function TemplatePicker({ open, onClose }: { open: boolean; onClose: () => void }) {
  const newDeckFromTemplate = useDeckStore((s) => s.newDeckFromTemplate);
  const [custom, setCustom] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadTemplates = () => {
    setLoading(true);
    setError(null);
    void api
      .listTemplates()
      .then((r) => setCustom(r.templates))
      .catch(() => setCustom([]))
      .finally(() => setLoading(false));
  };

  const start = async (id: string, builtin: boolean) => {
    setBusyId(id);
    setError(null);
    try {
      if (builtin) {
        const tpl = getBuiltinTemplate(id);
        if (!tpl) throw new Error('Template not found');
        const deckId = await newDeckFromTemplate(tpl.slides, tpl.title, tpl.theme);
        if (!deckId) throw new Error("Couldn't create the deck. Try again.");
        onClose();
        navigate(paths.edit(deckId));
        return;
      }
      const tpl = await api.getTemplate(id);
      const deckId = await newDeckFromTemplate(tpl.slides, tpl.title, tpl.theme);
      if (!deckId) throw new Error("Couldn't create the deck. Try again.");
      onClose();
      navigate(paths.edit(deckId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open template');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="tpl-title"
      slotProps={{ transition: { onEnter: loadTemplates } }}
    >
      <DialogTitle id="tpl-title" component="h2" variant="h6">
        Start from a template
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Approved AST starters. Pick one to create a private draft you can edit and share.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {loading && (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 2 }}>
              <CircularProgress size={24} aria-label="Loading templates" />
            </Box>
          )}
          <Stack spacing={1}>
            {BUILTIN_TEMPLATES.map((t) => (
              <Button
                key={t.id}
                variant="outlined"
                color="neutral"
                disabled={Boolean(busyId)}
                onClick={() => void start(t.id, true)}
                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.25 }}
                startIcon={busyId === t.id ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.description} · {t.slides.length} slides
                  </Typography>
                </Box>
              </Button>
            ))}
            {custom.map((t) => (
              <Button
                key={t.id}
                variant="outlined"
                color="neutral"
                disabled={Boolean(busyId)}
                onClick={() => void start(t.id, false)}
                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.25 }}
                startIcon={busyId === t.id ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.description || 'Company template'} · {t.slide_count ?? '?'} slides · {t.author_name}
                  </Typography>
                </Box>
              </Button>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="neutral" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
